import {
  INTAKE_PHOTO_LIMIT,
  INTAKE_PHOTO_MAX_EDGE,
  INTAKE_TIME_WINDOWS,
  INTAKE_WANTS,
  US_STATE_OPTIONS,
  formatPhoneInput,
  offeredIntakeDays,
} from "@roof-hawk/core";
import { useEffect, useMemo, useRef, useState } from "react";

import { COMPANY_NAME } from "@roof-hawk/core";
import { WORDMARK_ON_LIGHT } from "../../logo";

const FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/lead-link`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

/** Where our work can be seen. Jonathon, 2026-09-19. */
const YOUTUBE_URL = "https://www.youtube.com/@CopperRidgeRoofing";

/** `/go/<slug>`, and nothing that merely looks like it. An empty slug — the
 * bare `/go` — is the link with nobody on it, for a yard sign or a flyer. */
export function intakeSlugFromPath(pathname: string): string | null {
  const match = /^\/go(?:\/([a-z0-9][a-z0-9-]{1,31}))?\/?$/i.exec(pathname);
  if (!match) return null;
  return (match[1] ?? "").toLowerCase();
}

interface Owner {
  name: string;
  phone: string | null;
}

interface Shot {
  id: string;
  file: File;
  url: string;
}

/**
 * Scale a photograph down before it is uploaded.
 *
 * A modern phone's camera produces a four-thousand-pixel original of five or
 * six megabytes. Nobody looks at it at that size — not the rep on the job
 * screen, not CompanyCam — and the customer is standing in their own driveway
 * on a cell signal watching a progress bar. So the longest edge comes down to
 * 1600 and the bytes drop by roughly nine tenths.
 *
 * A file that will not decode is sent as it came. Refusing to upload somebody's
 * photograph because this browser could not draw it is the worse failure, and
 * the server has its own ceiling.
 */
async function scaleDown(file: File): Promise<File> {
  if (!file.type.startsWith("image/")) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const longest = Math.max(bitmap.width, bitmap.height);
    if (longest <= INTAKE_PHOTO_MAX_EDGE) {
      bitmap.close();
      return file;
    }
    const scale = INTAKE_PHOTO_MAX_EDGE / longest;
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const context = canvas.getContext("2d");
    if (!context) {
      bitmap.close();
      return file;
    }
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, "image/jpeg", 0.82);
    });
    if (!blob) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", {
      type: "image/jpeg",
    });
  } catch {
    return file;
  }
}

/**
 * The days the form offers, as the chips show them.
 *
 * Which days those are is `offeredIntakeDays` in packages/core — working days
 * only, no weekends and no holidays anybody keeps (Jonathon, 2026-09-22) —
 * because the server refuses anything else and the two must agree. This only
 * turns each one into the two lines on a chip.
 */
function dayChips(from: Date): { iso: string; weekday: string; day: number }[] {
  return offeredIntakeDays(from).map((iso) => {
    // Midday, so no timezone can shunt the label onto the day before.
    const date = new Date(`${iso}T12:00:00Z`);
    return {
      iso,
      weekday: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][
        date.getUTCDay()
      ]!,
      day: date.getUTCDate(),
    };
  });
}

/**
 * The page a customer opens from a text message.
 *
 * Jonathon, 2026-09-19: "i would like a link i can text a customer where they
 * can enter in their own info and their lead goes into roofboard."
 *
 * The third page in this app that belongs to somebody who is not staff, after
 * the customer's document and the storm report, and it follows the same rule:
 * no Auth, no install banner, no notification prompt. None of that belongs on
 * a stranger's phone, and `App` answers this route before it touches a session.
 *
 * Three screens rather than one, because a phone showing eleven fields at once
 * is a phone somebody closes. Each screen asks one thing, the progress bar
 * says how much is left, and only the first two fields are required — the rest
 * is what the rep would otherwise have to ring up and ask for.
 */
export function IntakePage({ slug }: { slug: string }) {
  const [owner, setOwner] = useState<Owner | null | "loading" | "none">(
    slug === "" ? "none" : "loading",
  );
  const [step, setStep] = useState(0);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [wants, setWants] = useState<string[]>([]);
  const [notes, setNotes] = useState("");
  const [days, setDays] = useState<string[]>([]);
  const [times, setTimes] = useState<string[]>([]);
  const [heardHow, setHeardHow] = useState("");
  /** The honeypot. Hidden from people, irresistible to form-filling scripts. */
  const [company, setCompany] = useState("");
  const [shots, setShots] = useState<Shot[]>([]);

  const photoInput = useRef<HTMLInputElement>(null);
  const openedAt = useRef(Date.now());
  const dayChoices = useMemo(() => dayChips(new Date()), []);

  useEffect(() => {
    document.title = `${COMPANY_NAME} — free inspection`;
  }, []);

  useEffect(() => {
    if (slug === "") return;
    let cancelled = false;
    void (async () => {
      try {
        const response = await fetch(
          `${FUNCTION_URL}?slug=${encodeURIComponent(slug)}`,
          {
            headers: { apikey: ANON_KEY, Authorization: `Bearer ${ANON_KEY}` },
          },
        );
        const body = await response.json().catch(() => null);
        if (cancelled) return;
        setOwner(
          body?.owner && typeof body.owner.name === "string"
            ? { name: body.owner.name, phone: body.owner.phone ?? null }
            : "none",
        );
      } catch {
        // A link whose owner cannot be read still takes a lead: the slug goes
        // with the submission and the server resolves it again. Better a form
        // with no name at the top than no form at all.
        if (!cancelled) setOwner("none");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Object URLs are revoked when the tile goes, and on the way out.
  useEffect(
    () => () => {
      for (const shot of shots) URL.revokeObjectURL(shot.url);
    },
    // Intentionally on unmount only: revoking on every change would kill the
    // URL of a photograph that is still on screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const toggle = (
    value: string,
    chosen: string[],
    set: (next: string[]) => void,
  ) => {
    set(
      chosen.includes(value)
        ? chosen.filter((item) => item !== value)
        : [...chosen, value],
    );
  };

  async function addPhotos(files: FileList | null) {
    if (!files) return;
    const room = INTAKE_PHOTO_LIMIT - shots.length;
    const taking = Array.from(files).slice(0, Math.max(room, 0));
    const scaled = await Promise.all(
      taking
        .filter((file) => file.type.startsWith("image/"))
        .map(async (file) => {
          const smaller = await scaleDown(file);
          return {
            id: `${Date.now()}-${Math.random()}`,
            file: smaller,
            url: URL.createObjectURL(smaller),
          };
        }),
    );
    setShots((current) => [...current, ...scaled]);
  }

  function dropPhoto(id: string) {
    setShots((current) => {
      const going = current.find((shot) => shot.id === id);
      if (going) URL.revokeObjectURL(going.url);
      return current.filter((shot) => shot.id !== id);
    });
  }

  async function send() {
    setSending(true);
    setProblem(null);
    try {
      const form = new FormData();
      form.set("slug", slug);
      form.set("openedAt", String(openedAt.current));
      form.set(
        "lead",
        JSON.stringify({
          firstName,
          lastName,
          // The production function still expects `name` until its separate
          // deployment. Keep leads arriving during that window.
          name: [firstName.trim(), lastName.trim()].filter(Boolean).join(" "),
          phone,
          email,
          address,
          city,
          state,
          zip,
          wants,
          notes,
          preferredDays: days,
          preferredTimes: times,
          heardHow,
          company,
        }),
      );
      for (const shot of shots) form.append("photo", shot.file);

      const response = await fetch(FUNCTION_URL, {
        method: "POST",
        headers: { apikey: ANON_KEY, Authorization: `Bearer ${ANON_KEY}` },
        body: form,
      });
      const body = await response.json().catch(() => null);

      if (response.ok && body?.ok) {
        setSent(true);
        return;
      }
      setProblem(
        typeof body?.error === "string"
          ? body.error
          : "That did not go through. Please try again, or give us a ring.",
      );
    } catch {
      setProblem(
        "That did not go through — check your signal and try again, or give us a ring.",
      );
    } finally {
      setSending(false);
    }
  }

  const repLine =
    owner && owner !== "loading" && owner !== "none"
      ? [owner.name, owner.phone].filter(Boolean).join(" · ")
      : null;

  const header = (
    <header className="ilink-head">
      <img src={WORDMARK_ON_LIGHT} alt={COMPANY_NAME} />
      {repLine ? <p className="ilink-rep">{repLine}</p> : null}
    </header>
  );

  if (sent) {
    return (
      <main className="ilink">
        <div className="ilink-card">
          {header}
          <div className="ilink-done">
            <div className="ilink-tick" aria-hidden="true">
              ✓
            </div>
            <h1>
              You&rsquo;re on the list{firstName ? `, ${firstName.trim()}` : ""}
              .
            </h1>
            <p>
              We have your details{shots.length > 0 ? " and your photos" : ""}
              {days.length > 0 ? " and the times that suit you" : ""}.
            </p>
          </div>

          <section className="ilink-next">
            <h2>What happens next</h2>
            <p>
              {owner && owner !== "loading" && owner !== "none"
                ? `${owner.name.split(" ")[0]} will`
                : "We will"}{" "}
              reach out as soon as possible to confirm your time and talk you
              through the next steps.
            </p>
          </section>

          <a
            className="ilink-tube"
            href={YOUTUBE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="ilink-tube-mark" aria-hidden="true">
              ▶
            </span>
            <span>
              <b>See our work</b>
              <span>Real jobs, start to finish, on YouTube</span>
            </span>
            <span className="ilink-tube-arrow" aria-hidden="true">
              ›
            </span>
          </a>
        </div>
      </main>
    );
  }

  if (owner === "loading") {
    return (
      <main className="ilink">
        <div className="ilink-card">{header}</div>
      </main>
    );
  }

  // A slug nobody holds is told plainly rather than shown a form that goes
  // nowhere. The bare `/go` is not that case: it is the link with no rep.
  if (slug !== "" && owner === "none") {
    return (
      <main className="ilink">
        <div className="ilink-card">
          {header}
          <div className="ilink-done">
            <h1>That link is no longer in use.</h1>
            <p>Give us a ring and we will get you booked in.</p>
          </div>
        </div>
      </main>
    );
  }

  const canContinue =
    firstName.trim() !== "" && phone.replace(/\D/g, "").length >= 10;

  return (
    <main className="ilink">
      <div className="ilink-card">
        {header}

        <div className="ilink-bar" aria-hidden="true">
          {[0, 1, 2].map((index) => (
            <i key={index} className={index <= step ? "on" : ""} />
          ))}
        </div>

        {step === 0 ? (
          <form
            className="ilink-form"
            onSubmit={(event) => {
              event.preventDefault();
              if (canContinue) setStep(1);
            }}
          >
            <h1>Free inspection, no charge</h1>
            <p className="ilink-sub">Three quick screens. About a minute.</p>

            <div className="ilink-pair">
              <label className="ilink-field" htmlFor="ilink-first">
                First name
                <input
                  id="ilink-first"
                  value={firstName}
                  autoComplete="given-name"
                  onChange={(event) => setFirstName(event.target.value)}
                />
              </label>
              <label className="ilink-field" htmlFor="ilink-last">
                Last name
                <input
                  id="ilink-last"
                  value={lastName}
                  autoComplete="family-name"
                  onChange={(event) => setLastName(event.target.value)}
                />
              </label>
            </div>

            <label htmlFor="ilink-phone">Cell number</label>
            <input
              id="ilink-phone"
              value={phone}
              inputMode="tel"
              autoComplete="tel"
              onChange={(event) =>
                setPhone(formatPhoneInput(event.target.value))
              }
            />

            <label htmlFor="ilink-email">Email</label>
            <input
              id="ilink-email"
              value={email}
              inputMode="email"
              autoComplete="email"
              onChange={(event) => setEmail(event.target.value)}
            />

            <label htmlFor="ilink-address">Street address</label>
            <input
              id="ilink-address"
              value={address}
              autoComplete="street-address"
              onChange={(event) => setAddress(event.target.value)}
            />

            <label htmlFor="ilink-city">City</label>
            <input
              id="ilink-city"
              value={city}
              autoComplete="address-level2"
              onChange={(event) => setCity(event.target.value)}
            />

            <div className="ilink-pair">
              <label className="ilink-field" htmlFor="ilink-state">
                State
                <select
                  id="ilink-state"
                  value={state}
                  autoComplete="address-level1"
                  onChange={(event) => setState(event.target.value)}
                >
                  <option value="">Select state</option>
                  {US_STATE_OPTIONS.map((option) => (
                    <option key={option.code} value={option.code}>
                      {option.code}
                    </option>
                  ))}
                </select>
              </label>
              <label className="ilink-field" htmlFor="ilink-zip">
                ZIP
                <input
                  id="ilink-zip"
                  value={zip}
                  inputMode="numeric"
                  autoComplete="postal-code"
                  maxLength={10}
                  onChange={(event) => setZip(event.target.value)}
                />
              </label>
            </div>

            {/* Not a real field. Hidden from people and from screen readers,
                left in the DOM for the scripts that fill everything in. */}
            <input
              className="ilink-trap"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={company}
              onChange={(event) => setCompany(event.target.value)}
            />

            <button className="ilink-go" type="submit" disabled={!canContinue}>
              Next
            </button>
            <p className="ilink-fine">
              Only ever used to reach you about this property.
            </p>
          </form>
        ) : null}

        {step === 1 ? (
          <form
            className="ilink-form"
            onSubmit={(event) => {
              event.preventDefault();
              setStep(2);
            }}
          >
            <h1>What should we look at?</h1>
            <p className="ilink-sub">Tap everything that applies.</p>

            <div
              className="ilink-chips"
              role="group"
              aria-label="What should we look at"
            >
              {INTAKE_WANTS.map((want) => (
                <button
                  key={want}
                  type="button"
                  className="ilink-chip"
                  aria-pressed={wants.includes(want)}
                  onClick={() => toggle(want, wants, setWants)}
                >
                  {want}
                </button>
              ))}
            </div>

            <label htmlFor="ilink-photos">
              Photos — the fastest way to get an answer
            </label>
            <div className="ilink-shots">
              {shots.map((shot) => (
                <div key={shot.id} className="ilink-shot">
                  <img src={shot.url} alt="" />
                  <button
                    type="button"
                    aria-label="Remove photo"
                    onClick={() => dropPhoto(shot.id)}
                  >
                    ×
                  </button>
                </div>
              ))}
              {shots.length < INTAKE_PHOTO_LIMIT ? (
                <button
                  type="button"
                  className="ilink-add-shot"
                  onClick={() => photoInput.current?.click()}
                >
                  + Add
                </button>
              ) : null}
              <input
                id="ilink-photos"
                ref={photoInput}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(event) => {
                  void addPhotos(event.target.files);
                  event.target.value = "";
                }}
              />
            </div>

            <label htmlFor="ilink-notes">Anything we should know</label>
            <textarea
              id="ilink-notes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />

            <button className="ilink-go" type="submit">
              Next
            </button>
            <button
              className="ilink-back"
              type="button"
              onClick={() => setStep(0)}
            >
              Back
            </button>
          </form>
        ) : null}

        {step === 2 ? (
          <form
            className="ilink-form"
            onSubmit={(event) => {
              event.preventDefault();
              void send();
            }}
          >
            <h1>When suits you?</h1>
            <p className="ilink-sub">
              Pick any days that work — we&rsquo;ll confirm one.
            </p>

            <label id="ilink-days-label">Days that work</label>
            <div
              className="ilink-chips ilink-days"
              role="group"
              aria-labelledby="ilink-days-label"
            >
              {dayChoices.map((choice) => (
                <button
                  key={choice.iso}
                  type="button"
                  className="ilink-chip"
                  aria-pressed={days.includes(choice.iso)}
                  onClick={() => toggle(choice.iso, days, setDays)}
                >
                  <b>{choice.weekday}</b>
                  <span>{choice.day}</span>
                </button>
              ))}
            </div>

            <label id="ilink-times-label">Time of day</label>
            <div
              className="ilink-chips"
              role="group"
              aria-labelledby="ilink-times-label"
            >
              {INTAKE_TIME_WINDOWS.map((window) => (
                <button
                  key={window}
                  type="button"
                  className="ilink-chip"
                  aria-pressed={times.includes(window)}
                  onClick={() => toggle(window, times, setTimes)}
                >
                  {window}
                </button>
              ))}
            </div>

            <label htmlFor="ilink-heard">How did you hear about us?</label>
            <input
              id="ilink-heard"
              value={heardHow}
              onChange={(event) => setHeardHow(event.target.value)}
            />

            {problem ? <p className="ilink-problem">{problem}</p> : null}

            <button className="ilink-go" type="submit" disabled={sending}>
              {sending ? "Sending…" : "Book my inspection"}
            </button>
            <button
              className="ilink-back"
              type="button"
              disabled={sending}
              onClick={() => setStep(1)}
            >
              Back
            </button>
          </form>
        ) : null}
      </div>
    </main>
  );
}
